import { useId } from 'react';
import { Input } from '@ai-tutor/ui';
import { sheetRowsSelection, unitCount } from '@/lib/sources/scopeSelection';
import type { OutlineSection } from '@/types/api';

interface SheetRowsInputProps {
  section: OutlineSection;
  value: string;
  onChange: (text: string) => void;
}

/**
 * Narrow a ticked sheet to a row range ("1-2000"). Empty keeps every row;
 * only the rows kept are parsed (and billed).
 */
export function SheetRowsInput({
  section,
  value,
  onChange,
}: SheetRowsInputProps) {
  const id = useId();
  const pick = sheetRowsSelection(section, value);
  return (
    <div className='mb-1 ml-[3.25rem] mr-3'>
      <label htmlFor={id} className='sr-only'>
        Rows to use from {section.title}
      </label>
      <Input
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={`All rows (1-${(section.rows ?? 0).toLocaleString()})`}
        inputMode='numeric'
        aria-invalid={Boolean(pick.error)}
        aria-describedby={`${id}-hint`}
        className='h-11 text-sm sm:h-9'
      />
      <p id={`${id}-hint`} className='mt-1 text-xs text-muted-foreground'>
        {pick.error ? (
          <span className='text-destructive'>{pick.error}</span>
        ) : value.trim() ? (
          `About ${unitCount('cell', pick.cells)}`
        ) : (
          'Type a row range to use part of this sheet'
        )}
      </p>
    </div>
  );
}
